import {
	Box,
	Button,
	Card as MuiCard,
	CardContent,
	Checkbox,
	Chip,
	Divider,
	Fade,
	FormControlLabel,
	Grid,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { spacing } from "@material-ui/system";
import { FC, useRef, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components/macro";
import {
	TABLE_LIMITS,
	TABLE_LIMIT_DEFAULT,
} from "../../constants";
import {
	GenerateProyectsPdf,
	GenerateProjectsPdfRef,
} from "../GenerateProyectsPdf";
import {AnyProject, BaseProjectLegacy, ProjectStatus, ResponsableProyecto} from "../../models/Proyecto";
import firebase from "firebase/app";
import "firebase/firestore";
import { RevisionTableRow } from "./TableRow";
import { Usuario } from "src/models/Usuario";

import { actualizarResponsableRevisiones } from "../../services/empresa/revisiones";
import { ResponsibleDialog } from "./ResponsibleDialog";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/store/reducer";
import { IBusinessState } from "../../redux/reducers/businessReducer";
import { Info } from "@material-ui/icons";
import { ProjectCounter } from "../CheckPro/ProjectCounter";

const Card = styled(MuiCard)(spacing);

interface RevisionesTableProps {
	type: "revisiones" | "revisiones-checklist";
	enableSelection?: boolean;
	handleOpenEditDialog: (selected: any) => void;
	handleOpenConfirmDialog: (selected: any) => void;
	handleOpenResponsiblesDialog: (selected: any) => void;
	openNewDialog?: () => void;
	revisiones: AnyProject[];
	isLoading: boolean;
	canEdit?: boolean;
	onArrowClick: (selected: AnyProject) => void;
	renderPdf?: boolean;
	renderExcelSumary?: boolean;
	hidePercentage?: boolean;
	canRestore?: boolean;
}

export const RevisionesTable: FC<RevisionesTableProps> = ({
	type,
	canEdit = false,
	handleOpenEditDialog,
	revisiones,
	isLoading,
	handleOpenConfirmDialog,
	handleOpenResponsiblesDialog,
	onArrowClick,
	renderPdf = false,
	renderExcelSumary = false,
	hidePercentage,
	canRestore = false,
}) => {
	const [showDeleted, setShowDeleted] = useState(false);
	const [page, setPage] = useState(0);
	const [limit, setLimit] = useState(TABLE_LIMIT_DEFAULT);
	const [selectedRevisiones, setSelectedRevisiones] =
		useState<AnyProject[]>([]);

	const [openDialog, setOpenDialog] = useState(false);

	const pdfDialogref =
		useRef<null | GenerateProjectsPdfRef>(null);

	const {
		edit: { selectedBusiness },
	} = useSelector<RootState, IBusinessState>(
		(state) => state.businessReducer
	);

	/**
	 * Filtra las revisiones según la visibilidad,
	 * es decir mostrar activas o eliminadas.
	 */
	// Type guards para verificar propiedades en los proyectos
	const hasEstadoProperty = (project: AnyProject): project is AnyProject & { Estado: ProjectStatus } => {
		return 'Estado' in project;
	};

	const hasNombreProyectoProperty = (project: AnyProject): project is AnyProject & { NombreProyecto: string } => {
		return 'NombreProyecto' in project;
	};

	// Type guard para verificar si un proyecto es compatible con BaseProjectLegacy
	const isBaseProjectLegacy = (project: AnyProject): project is BaseProjectLegacy => {
		return 'Estado' in project &&
			   'NombreProyecto' in project &&
			   'ComandoOpciones' in project &&
			   'DiasPorVencer' in project &&
			   'DiasVencidos' in project;
	};

	// Función para adaptar un AnyProject a BaseProjectLegacy
	const adaptToBaseProjectLegacy = (project: AnyProject): BaseProjectLegacy => {
		// Si ya es un BaseProjectLegacy, devolverlo tal cual
		if (isBaseProjectLegacy(project)) {
			return project;
		}

		// Determinar el tipo basado en las propiedades del proyecto
		let tipoAsignable: "" | "CheckList" | "Revision" = "";

		// Si hay una propiedad Tipo, intentamos determinar qué tipo es
		if ('Tipo' in project) {
			const tipo = (project as any).Tipo;
			if (tipo === "CheckList" || tipo === "Revision") {
				tipoAsignable = tipo;
			} else if (tipo === "Edificios" || tipo === "Casas" || tipo === "Otro") {
				// Si es un ChecklistProject, asignar Tipo como "CheckList"
				tipoAsignable = "CheckList";
			}
		}

		// Crear un objeto que cumpla con la estructura mínima de BaseProjectLegacy
		console.log('🔧 [adaptToBaseProjectLegacy] Original project data:', {
			id: project.id,
			DiasPorVencer: (project as any).DiasPorVencer,
			DiasVencidos: (project as any).DiasVencidos,
			DiasPorVencer_type: typeof (project as any).DiasPorVencer,
			DiasVencidos_type: typeof (project as any).DiasVencidos
		});
		
		const baseProject: BaseProjectLegacy = {
			id: project.id,
			ComandoOpciones: {},
			DiasPorVencer: (project as any).DiasPorVencer ?? 0,
			DiasVencidos: (project as any).DiasVencidos ?? 0,
			EmpresaReference: hasEmpresaReferenceProperty(project) ? project.EmpresaReference : null as any,
			Estado: hasEstadoProperty(project) ? project.Estado : "Activo" as ProjectStatus,
			FechaCreacion: project.FechaCreacion || null as any,
			FechaCreacionString: hasFechaCreacionStringProperty(project)
				? project.FechaCreacionString
				: "",
			FirebaseId: hasFirebaseIdProperty(project) ? project.FirebaseId : "",
			NombreProyecto: hasNombreProyectoProperty(project) ? project.NombreProyecto : project.Nombre,
			NombreProyecto_lower: hasNombreProyectoProperty(project) ? project.NombreProyecto.toLowerCase() : project.Nombre.toLowerCase(),
			Responsable: hasResponsableProperty(project) ? project.Responsable : {
				NombreCompleto: "Sin Responsable",
				Cargo: "",
				FirebaseId: "",
				FechaAsignado: null as any,
				NombreCompleto_lower: "sin responsable"
			} as ResponsableProyecto,
			Responsables: hasResponsablesProperty(project) ? project.Responsables : [],
			Tipo: hasValidTipoProperty(project) ? (project as any).Tipo : tipoAsignable,
			AlertaEliminacion: false,
			Carpetas: project.Carpetas || 0,
			Resueltas: (project as any).Resueltas || 0,
			TotalRespuestas: (project as any).TotalRespuestas || 0
		};

		console.log('🔧 [adaptToBaseProjectLegacy] Adapted project data:', {
			id: baseProject.id,
			DiasPorVencer: baseProject.DiasPorVencer,
			DiasVencidos: baseProject.DiasVencidos,
			DiasPorVencer_type: typeof baseProject.DiasPorVencer,
			DiasVencidos_type: typeof baseProject.DiasVencidos
		});

		return baseProject;
	};

	// Type guards para propiedades específicas
	const hasFechaCreacionStringProperty = (project: AnyProject): project is AnyProject & { FechaCreacionString: string } => {
		return 'FechaCreacionString' in project;
	};

	const hasFirebaseIdProperty = (project: AnyProject): project is AnyProject & { FirebaseId: string } => {
		return 'FirebaseId' in project;
	};

	const hasEmpresaReferenceProperty = (project: AnyProject): project is AnyProject & { EmpresaReference: firebase.firestore.DocumentReference<any> } => {
		return 'EmpresaReference' in project;
	};

	const hasResponsableProperty = (project: AnyProject): project is AnyProject & { Responsable: ResponsableProyecto } => {
		return 'Responsable' in project && project.Responsable !== null && project.Responsable !== undefined;
	};

	const hasResponsablesProperty = (project: AnyProject): project is AnyProject & { Responsables: string[] } => {
		return 'Responsables' in project && Array.isArray(project.Responsables);
	};

	// Ya no necesitamos este type guard específico, usaremos una verificación más directa
	const hasValidTipoProperty = (project: any): boolean => {
		return 'Tipo' in project && (project.Tipo === "CheckList" || project.Tipo === "Revision");
	};

	const hasCarpetasProperty = (project: AnyProject): project is AnyProject & { Carpetas: string | number } => {
		return 'Carpetas' in project;
	};

	const revisionesToShow = useMemo(
		() =>
			revisiones?.filter((c) =>
				hasEstadoProperty(c) ? (
					showDeleted
						? c.Estado === "Eliminado"
						: c.Estado === "Activo"
				) : !showDeleted // Si no tiene Estado, solo mostrarlo cuando no estemos viendo eliminados
			) || [],
		[revisiones, showDeleted]
	);

	//////////////////////////////////////////////
	// 	Lógica de selección múltiple
	//////////////////////////////////////////////

	const allSelected = useMemo(
		() =>
			revisiones
				?.filter((e) => !hasEstadoProperty(e) || e.Estado === "Activo")
				.every((e) =>
					selectedRevisiones.find((e2) => e2.id === e.id)
				),
		[selectedRevisiones, revisiones]
	);

	const handleToggleAll = () =>
		setSelectedRevisiones(
			allSelected
				? []
				: revisiones.filter((c) => !hasEstadoProperty(c) || c.Estado === "Activo")
		);

	const handleToggleSelected = (id: string) => {
		const clone = [...selectedRevisiones];
		const found = clone.find((e) => e.id === id);
		if (found) clone.splice(clone.indexOf(found), 1);
		else clone.push(revisiones.find((e) => e.id === id)!);
		setSelectedRevisiones(clone);
	};

	//////////////////////////////////////////////
	// 	Lógica de conteo de revisiones
	//////////////////////////////////////////////

	const utilizados = useMemo(
		() =>
			revisiones?.filter((r) => !hasEstadoProperty(r) || r.Estado === "Activo")
				.length,
		[revisiones]
	);

	const projectLimit = useMemo(() => {
		if (!selectedBusiness) return NaN;
		const { Revisiones, CheckList } = selectedBusiness;
		if (type === "revisiones")
			return Revisiones.LimiteRevisiones;
		else if (type === "revisiones-checklist")
			return CheckList.LimiteCheckList;
		else return NaN;
	}, [selectedBusiness, type]);

	//////////////////////////////////////////////

	const handleSetResponsable = async (
		user: Usuario | null
	) => {
		if (!user || !user.id) return;
		try {
			await actualizarResponsableRevisiones(
				user,
				...selectedRevisiones
			);
		} catch (error: any) {
			console.error(error);
			alert(error.message);
		}
	};

	return (
		<>
			<Card
				mb={6}
				style={{ display: "flex", flexDirection: "column" }}
			>
				{!hidePercentage && (
					<>
						{/* barra de uso */}
						<ProjectCounter
							limit={projectLimit}
							used={utilizados}
						/>

						<Divider />
					</>
				)}

				{canEdit && (
					<>
						<CardContent>
							<Grid
								item
								container
								justify="space-between"
								sm={12}
							>
								<Grid item>
									<Button
										disabled={
											selectedRevisiones.length === 0
										}
										variant="contained"
										color="primary"
										onClick={() => {
											setOpenDialog(true);
										}}
									>
										Designar Responsable
									</Button>
								</Grid>

								{canRestore && (
									<Grid item>
										<FormControlLabel
											control={
												<Checkbox
													id="mostrar-eliminados-checkbox"
													checked={showDeleted}
													onChange={() =>
														setShowDeleted(!showDeleted)
													}
													color="primary"
												/>
											}
											label="Mostrar Eliminados"
										/>
									</Grid>
								)}
							</Grid>
						</CardContent>
					</>
				)}
				<CardContent>
					<TableContainer>
						<Table stickyHeader size="small">
							<TableHead>
								<TableRow>
									{canEdit && !showDeleted && (
										<TableCell>
											<Checkbox
												color="primary"
												checked={allSelected}
												onChange={handleToggleAll}
												inputProps={{
													"aria-label":
														"secondary checkbox",
												}}
											/>
										</TableCell>
									)}
									<TableCell>Nombre</TableCell>
									<TableCell>Revisiones</TableCell>
									{type === "revisiones-checklist" && (
										<TableCell>Observaciones</TableCell>
									)}
									<TableCell>Responsable</TableCell>
									<TableCell>Cargo</TableCell>
									<TableCell>Fecha Creación</TableCell>
									<TableCell align="center">
										Acciones
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{!isLoading
									? revisionesToShow
									                .sort((a, b) => {
									                        // Si ambos tienen NombreProyecto, ordenar por ese campo
									                        if (hasNombreProyectoProperty(a) && hasNombreProyectoProperty(b)) {
									                                return a.NombreProyecto.localeCompare(b.NombreProyecto);
									                        }
									                        // Si solo uno tiene NombreProyecto, poner primero el que lo tiene
									                        if (hasNombreProyectoProperty(a)) return -1;
									                        if (hasNombreProyectoProperty(b)) return 1;

									                        // Si ninguno tiene NombreProyecto, usar Nombre como alternativa
									                        return a.Nombre.localeCompare(b.Nombre);
									                }
											)
											.slice(
												page * limit,
												page * limit + limit
											)
											.map((revision) => (
												<Fade key={revision.id} in={true}>
													<RevisionTableRow
														renderExcelSumary={
															renderExcelSumary
														}
														renderPdf={renderPdf}
														project={adaptToBaseProjectLegacy(revision)}
														selected={
															!!selectedRevisiones.find(
																(r) => r.id === revision.id
															)
														}
														handleOpenEditDialog={(
															project
														) =>
															handleOpenEditDialog(project)
														}
														handleOpenConfirmDialog={(
															project
														) =>
															handleOpenConfirmDialog(
																project
															)
														}
														handleOpenResponsiblesDialog={(
															project
														) =>
															handleOpenResponsiblesDialog(
																project
															)
														}
														handleChecksButton={(project) =>
															onArrowClick(project)
														}
														onSelection={
															handleToggleSelected
														}
														openPdfDialog={(project) => {
															pdfDialogref.current?.openDialog(
																project
															);
														}}
													/>
												</Fade>
											))
									: new Array(limit).fill(0).map((x, i) => (
											<TableRow key={i}>
												<TableCell colSpan={10}>
													<Skeleton />
												</TableCell>
											</TableRow>
									  ))}
							</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						component="div"
						count={revisionesToShow.length}
						onChangePage={(_, i) => setPage(i)}
						onChangeRowsPerPage={(e) =>
							setLimit(+e.target.value)
						}
						page={page}
						rowsPerPage={limit}
						rowsPerPageOptions={TABLE_LIMITS}
					/>
				</CardContent>
			</Card>

			<ResponsibleDialog
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				onSubmit={handleSetResponsable}
			/>
			{renderPdf ? (
				<GenerateProyectsPdf ref={pdfDialogref} />
			) : null}
		</>
	);
};
